import type { CampaignCreationCapabilities, SponsoredBrandsCreationSubmit } from "@adpilot/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  fetchSponsoredBrandProductEligibility,
  fetchSponsoredBrandsDiscovery,
} from "@/lib/campaign-creation-api";
import { filterEligibleStores, selectCreativeAssets } from "./sponsored-brands-discovery";
import { initialName, parseAsins } from "./sponsored-brands-format";
import {
  type AdFormat,
  type PlacementField,
  type ProductCollectionLandingPage,
  type SponsoredBrandDetailsForm,
  SponsoredBrandDetailsFormSchema,
  type VideoLandingPage,
} from "./sponsored-brands-model";
import { buildSponsoredBrandsPayload } from "./sponsored-brands-payload";
import { useSponsoredBrandsTargeting } from "./use-sponsored-brands-targeting";

interface UseSponsoredBrandsFormOptions {
  shopId: string;
  requestId: string;
  capabilities: CampaignCreationCapabilities;
  onSubmit: (payload: SponsoredBrandsCreationSubmit) => void;
}

/**
 * State, Amazon discovery data and submit validation for the Sponsored Brands creation form.
 * Sections stay presentational; every cross-section reset (brand → store → page → products)
 * lives here so the dependency chain is visible in one place.
 */
export function useSponsoredBrandsForm({
  shopId,
  requestId,
  capabilities,
  onSubmit,
}: UseSponsoredBrandsFormOptions) {
  const { t } = useTranslation("campaignCreation");
  const detailsForm = useForm<SponsoredBrandDetailsForm>({
    resolver: zodResolver(SponsoredBrandDetailsFormSchema),
    defaultValues: {
      campaignName: initialName(t("sb.defaults.campaign")),
      startDate: "",
      endDate: "",
      budget: "",
      brandId: null,
      adGroupName: initialName(t("sb.defaults.adGroup")),
      adName: initialName(t("sb.defaults.productCollectionAd")),
      brandName: "",
      headline: "",
      logoAssetId: null,
      customImageAssetId: null,
      videoAssetId: null,
    },
  });
  const details = detailsForm.watch();
  const { brandId } = details;
  const setCampaignName = (value: string) =>
    detailsForm.setValue("campaignName", value, { shouldDirty: true });
  const setStartDate = (value: string) =>
    detailsForm.setValue("startDate", value, { shouldDirty: true });
  const setEndDate = (value: string) =>
    detailsForm.setValue("endDate", value, { shouldDirty: true });
  const setBudget = (value: string) => detailsForm.setValue("budget", value, { shouldDirty: true });
  const setBrandId = useCallback(
    (value: string | null) => detailsForm.setValue("brandId", value, { shouldDirty: true }),
    [detailsForm],
  );
  const setAdGroupName = (value: string) =>
    detailsForm.setValue("adGroupName", value, { shouldDirty: true });
  const setAdName = (value: string) => detailsForm.setValue("adName", value, { shouldDirty: true });
  const setBrandName = (value: string) =>
    detailsForm.setValue("brandName", value, { shouldDirty: true });
  const setHeadline = (value: string) =>
    detailsForm.setValue("headline", value, { shouldDirty: true });
  const setLogoAssetId = (value: string | null) =>
    detailsForm.setValue("logoAssetId", value, { shouldDirty: true });
  const setCustomImageAssetId = (value: string | null) =>
    detailsForm.setValue("customImageAssetId", value, { shouldDirty: true });
  const setVideoAssetId = (value: string | null) =>
    detailsForm.setValue("videoAssetId", value, { shouldDirty: true });
  const [adFormat, setAdFormat] = useState<AdFormat>("productCollection");
  const [landingPages, setLandingPages] = useState({
    productCollection: "amazonStore" as ProductCollectionLandingPage,
    video: "productDetailPage" as VideoLandingPage,
  });
  const [automatedBidding, setAutomatedBidding] = useState(true);
  const [placementAdjustmentsEnabled, setPlacementAdjustmentsEnabled] = useState(false);
  const [placementValues, setPlacementValues] = useState<Record<PlacementField, string>>({
    topOfSearch: "",
    home: "",
    productPage: "",
    restOfSearch: "",
  });
  const [storeEntityId, setStoreEntityId] = useState<string | null>(null);
  const [storePageUrl, setStorePageUrl] = useState<string | null>(null);
  const [advertisedAsins, setAdvertisedAsins] = useState<string[]>([]);
  const [creativeAsins, setCreativeAsins] = useState<string[]>([]);
  const [productInput, setProductInput] = useState("");
  const [spotlightPageIds, setSpotlightPageIds] = useState<string[]>([]);
  const [spotlightPageAsins, setSpotlightPageAsins] = useState<Record<string, string>>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const discovery = useQuery({
    queryKey: ["campaign-creation", "sponsored-brands", "discovery", shopId],
    queryFn: () => fetchSponsoredBrandsDiscovery(shopId),
    retry: false,
  });
  const eligibility = useMutation({
    mutationFn: (asins: string[]) =>
      fetchSponsoredBrandProductEligibility(
        shopId,
        asins.map((asin) => ({ asin })),
      ),
  });
  const recommendationAsins = advertisedAsins.length > 0 ? advertisedAsins : creativeAsins;
  const targeting = useSponsoredBrandsTargeting({
    shopId,
    adFormat,
    recommendationAsins,
    bidCapabilities: capabilities.bid,
  });

  useEffect(() => {
    if (brandId || !discovery.data?.brands.length) return;
    const brand = discovery.data.brands[0];
    if (!brand) return;
    setBrandId(brand.brandId);
  }, [brandId, discovery.data?.brands, setBrandId]);
  const selectedBrand = discovery.data?.brands.find((brand) => brand.brandId === brandId) ?? null;

  const stores = useMemo(
    () => filterEligibleStores(discovery.data?.stores ?? [], brandId, adFormat),
    [adFormat, brandId, discovery.data?.stores],
  );
  const selectedStore = stores.find((store) => store.entityId === storeEntityId) ?? null;
  const { logoAssets, customImageAssets, videoAssets } = selectCreativeAssets(
    discovery.data?.assets ?? [],
  );

  const landingPage = adFormat === "video" ? landingPages.video : landingPages.productCollection;
  const landingSelectionComplete =
    adFormat === "storeSpotlight"
      ? Boolean(storeEntityId)
      : landingPage === "amazonStore"
        ? Boolean(storeEntityId && storePageUrl)
        : true;
  const showsProducts =
    adFormat === "productCollection"
      ? landingPage === "newLandingPage"
      : adFormat === "video" && landingSelectionComplete;
  const showsCreative =
    adFormat === "productCollection" && landingPage === "amazonStore"
      ? Boolean(storeEntityId)
      : landingSelectionComplete;
  const selectedLandingStorePage = selectedStore?.pages.find((page) => page.url === storePageUrl);
  // Product Collection ads landing on a Store page may only feature that page's eligible ASINs.
  const storeCreativeEligibility = useQuery({
    queryKey: [
      "campaign-creation",
      "sponsored-brands",
      "store-page-eligibility",
      shopId,
      selectedLandingStorePage?.id,
      selectedLandingStorePage?.asins,
    ],
    queryFn: async () => {
      const asins = selectedLandingStorePage?.asins ?? [];
      const results = [];
      for (let offset = 0; offset < asins.length; offset += 50) {
        results.push(
          ...(await fetchSponsoredBrandProductEligibility(
            shopId,
            asins.slice(offset, offset + 50).map((asin) => ({ asin })),
          )),
        );
      }
      return results;
    },
    enabled:
      adFormat === "productCollection" &&
      landingPage === "amazonStore" &&
      Boolean(selectedLandingStorePage?.asins.length),
    retry: false,
  });
  const creativeProductAsins = useMemo(
    () =>
      adFormat === "productCollection" && landingPage === "amazonStore"
        ? (storeCreativeEligibility.data ?? [])
            .filter((result) => result.status !== "INELIGIBLE")
            .map((result) => result.asin)
        : advertisedAsins,
    [adFormat, advertisedAsins, landingPage, storeCreativeEligibility.data],
  );
  useEffect(() => {
    setCreativeAsins((current) => current.filter((asin) => creativeProductAsins.includes(asin)));
  }, [creativeProductAsins]);

  const addAdvertisedProducts = async (value = productInput) => {
    const maximum = adFormat === "video" ? 1 : 100;
    const requested = parseAsins(value).filter((asin) => !advertisedAsins.includes(asin));
    const storePageAsins =
      landingPage === "amazonStore" ? new Set(selectedLandingStorePage?.asins ?? []) : null;
    const permitted = requested.filter((asin) => !storePageAsins || storePageAsins.has(asin));
    const outsideStorePage = requested.length - permitted.length;
    if (outsideStorePage > 0) {
      toast.error(t("sb.products.notOnStorePage", { count: outsideStorePage }));
    }
    const candidates = permitted
      .filter((asin) => !advertisedAsins.includes(asin))
      .slice(0, maximum - advertisedAsins.length);
    if (candidates.length === 0) return;
    const results = [];
    for (let offset = 0; offset < candidates.length; offset += 50) {
      results.push(...(await eligibility.mutateAsync(candidates.slice(offset, offset + 50))));
    }
    const eligible = results
      .filter((result) => result.status !== "INELIGIBLE")
      .map((result) => result.asin);
    const rejected = results.length - eligible.length;
    setAdvertisedAsins((current) => [...current, ...eligible].slice(0, maximum));
    setProductInput("");
    if (rejected > 0) toast.error(t("sb.products.notEligible", { count: rejected }));
  };

  const removeAdvertisedAsin = (asin: string) =>
    setAdvertisedAsins((current) => current.filter((item) => item !== asin));

  const selectBrand = (value: string | null) => {
    setBrandId(value);
    setStoreEntityId(null);
    setStorePageUrl(null);
  };

  const selectAdFormat = (nextFormat: AdFormat) => {
    setAdFormat(nextFormat);
    setAdName(initialName(t(`sb.defaults.${nextFormat}Ad`)));
  };

  const selectVideoLandingPage = (value: VideoLandingPage) =>
    setLandingPages((current) => ({ ...current, video: value }));

  const selectProductCollectionLandingPage = (value: ProductCollectionLandingPage) => {
    setLandingPages((current) => ({ ...current, productCollection: value }));
    setAdvertisedAsins([]);
    setCreativeAsins([]);
  };

  const selectStore = (value: string | null) => {
    setStoreEntityId(value);
    setStorePageUrl(null);
    setSpotlightPageIds([]);
    setSpotlightPageAsins({});
  };

  // Store Spotlight has no page picker; it lands on the selected store's first page.
  const selectSpotlightStore = (value: string | null) => {
    const store = stores.find((candidate) => candidate.entityId === value);
    setStoreEntityId(value);
    setStorePageUrl(store?.pages[0]?.url ?? null);
    setSpotlightPageIds([]);
    setSpotlightPageAsins({});
  };

  const selectStorePage = (value: string | null) => {
    setStorePageUrl(value);
    setAdvertisedAsins([]);
    setCreativeAsins([]);
  };

  const setPlacementValue = (key: PlacementField, value: string) =>
    setPlacementValues((current) => ({ ...current, [key]: value }));

  const payloadResult = buildSponsoredBrandsPayload(
    {
      requestId,
      details,
      selectedBrand,
      adFormat,
      landingPage,
      storeEntityId,
      storePageUrl,
      advertisedAsins,
      creativeAsins,
      selectedStore,
      spotlightPageIds,
      spotlightPageAsins,
      targetingType: targeting.targetingType,
      keywordRows: targeting.keywordRows,
      negativeKeywords: targeting.negativeKeywords,
      productTargetAsins: targeting.productTargetAsins,
      categoryTargets: targeting.categoryTargets,
      productTargetBid: targeting.productTargetBid,
      negativeProductAsins: targeting.negativeProductAsins,
      automatedBidding,
      placementAdjustmentsEnabled,
      placementValues,
    },
    capabilities,
  );
  const readyPayload = payloadResult.success ? payloadResult.data : null;
  const fieldError = (...paths: string[]) =>
    showValidationErrors &&
    !payloadResult.success &&
    payloadResult.error.issues.some((issue) => {
      const issuePath = issue.path.join(".");
      return paths.some((path) => issuePath.startsWith(path));
    })
      ? t("sb.validation.required")
      : undefined;

  // Save stays enabled; an invalid attempt reveals field-level errors instead.
  const submit = () => {
    if (readyPayload) {
      onSubmit(readyPayload);
      return;
    }
    setShowValidationErrors(true);
  };

  return {
    discovery,
    details,
    setCampaignName,
    setStartDate,
    setEndDate,
    setBudget,
    selectBrand,
    setAdGroupName,
    setAdName,
    setBrandName,
    setHeadline,
    setLogoAssetId,
    setCustomImageAssetId,
    setVideoAssetId,
    automatedBidding,
    setAutomatedBidding,
    placementAdjustmentsEnabled,
    setPlacementAdjustmentsEnabled,
    placementValues,
    setPlacementValue,
    adFormat,
    selectAdFormat,
    landingPage,
    selectVideoLandingPage,
    selectProductCollectionLandingPage,
    stores,
    selectedStore,
    storeEntityId,
    storePageUrl,
    selectStore,
    selectSpotlightStore,
    selectStorePage,
    showsProducts,
    showsCreative,
    productInput,
    setProductInput,
    advertisedAsins,
    addAdvertisedProducts,
    removeAdvertisedAsin,
    eligibilityPending: eligibility.isPending,
    logoAssets,
    customImageAssets,
    videoAssets,
    creativeProductAsins,
    creativeAsins,
    setCreativeAsins,
    spotlightPageIds,
    setSpotlightPageIds,
    spotlightPageAsins,
    setSpotlightPageAsins,
    recommendationAsins,
    targeting,
    fieldError,
    hasValidationErrors: showValidationErrors && !payloadResult.success,
    submit,
  };
}
